import {
  type AIProviderId,
  defaultChatModel,
  defaultFastModel,
  type EffortLevel,
  getAIProvider,
  isEffortLevel,
  resolveAIProviderId,
} from "../../../shared/aiProviders";
import { emptyCredentials } from "../../../shared/aiProviders/manifest";
import type { AiProvider as AiConfig } from "../../../shared/generatedDrizzleSchema";

export type AIConfigDraft = {
  provider: AIProviderId;
  credentials: Record<string, string>;
  chatModel: string;
  fastModel: string;
  effort: EffortLevel | null;
  connectedAppId: string | null;
};

export function emptyDraft(provider: AIProviderId = "openai"): AIConfigDraft {
  return {
    provider,
    credentials: emptyCredentials(getAIProvider(provider).credentialFields),
    chatModel: "",
    fastModel: "",
    effort: null,
    connectedAppId: null,
  };
}

export function toDraft(
  config: AiConfig,
  connectedAppId: string | null = null
): AIConfigDraft {
  const provider = resolveAIProviderId(config);
  const definition = getAIProvider(provider);
  return {
    provider,
    credentials: definition.unpackCredentials({
      baseUrl: config.baseUrl ?? "",
      apiKey: "",
    }),
    chatModel: config.chatModel ?? "",
    fastModel: config.fastModel ?? "",
    effort: isEffortLevel(config.effort) ? config.effort : null,
    connectedAppId,
  };
}

// The env-managed provider has no ai_providers row; its draft is derived from
// the provider manifest and shown read-only.
export function managedDraft(provider: AIProviderId): AIConfigDraft {
  const definition = getAIProvider(provider);
  return {
    provider,
    credentials: emptyCredentials(definition.credentialFields),
    chatModel: defaultChatModel(provider),
    fastModel: defaultFastModel(provider),
    effort: isEffortLevel(definition.defaultChatEffort)
      ? definition.defaultChatEffort
      : null,
    connectedAppId: null,
  };
}

export function toMutationArgs(
  id: string,
  active: boolean,
  draft: AIConfigDraft,
  connectorBaseUrl?: string | null
) {
  const definition = getAIProvider(draft.provider);
  const packed = definition.packCredentials(draft.credentials);
  // The modal hides Base URL for connector-backed providers, so the endpoint
  // comes from the selected connector rather than the (empty) draft field.
  const baseUrl =
    definition.endpointFromConnector && connectorBaseUrl
      ? connectorBaseUrl.trim()
      : packed.baseUrl;
  return {
    id,
    active,
    provider: draft.provider,
    baseUrl,
    caCertificate: packed.caCertificate,
    chatModel: draft.chatModel.trim(),
    fastModel: draft.fastModel.trim(),
    effort: draft.effort,
    connectedAppId: draft.connectedAppId,
  };
}

export function saveErrorMessage(error: unknown) {
  const message = error instanceof Error ? error.message : String(error);
  return `AI provider couldn’t be saved: ${message}`;
}

export function draftSaveDisabled(
  draft: AIConfigDraft,
  reusePrior: boolean
): boolean {
  const draftProvider = getAIProvider(draft.provider);
  const effectiveChatModel =
    draft.chatModel.trim() || defaultChatModel(draft.provider);
  const usesConnector = draft.connectedAppId != null;
  const missingRequired = draftProvider.credentialFields.some((field) => {
    if (!field.required) return false;
    if (field.sensitive && (reusePrior || usesConnector)) return false;
    return !draft.credentials[field.key]?.trim();
  });
  return (
    missingRequired ||
    (draftProvider.requiresChatModel && !effectiveChatModel.trim())
  );
}
